<template>
  <div>
    <v-card class="white elevation-3 ma-4">
      <toolbar
        :loading="loading"
        @search="search"
      />
    </v-card>

    <v-card
        class="white elevation-3 ma-4"
        :loading="loading"
    >
      <v-tabs v-model="tabIndex">
        <v-tab
            v-for="item in tabItems"
            :key="'tab-' + item.key"
            :disabled="item.disabled"
            active-class="primary--text"
            class="subheading text-capitalize tab-item"
        >
          {{ item.name }}
        </v-tab>
      </v-tabs>
      <v-divider class="mb-5"/>

      <v-tabs-items v-model="tabIndex">
        <v-tab-item
          v-for="item in tabItems"
          :key="'tab-item-' + item.key"
        >
          <template v-if="item.key === 'incidents'">
            <incidents-table
              :items="incidents"
              :total-items="incidentsTotal"
              :loading="!loading && loadingIncidents"
              :show-location-column="true"
              :show-client-column="false"
              typeData="incidents"
              :downloadJSON="false"
              @updatePagination="searchIncidents"
              @goToIncident="goToIncident"
            />
          </template>
          <template v-else-if="item.key === 'transmitted'">
            <incidents-table
              :items="transmitted"
              :total-items="transmittedTotal"
              :loading="!loading && loadingTransmitted"
              :show-location-column="false"
              :show-first-name-column="false"
              :show-client-column="false"
              typeData="transmitted"
              :downloadJSON="false"
              @updatePagination="searchTransmitted"
              @goToIncident="goToIncidentsCloud"
            />
          </template>
          <template v-else-if="item.key === 'airdropped'">
            <incidents-table
                :items="airdropped"
                :total-items="airdroppedTotal"
                :loading="!loading && loadingAirDropped"
                :show-location-column="false"
                :show-first-name-column="false"
                :show-client-column="false"
                typeData="airdropped"
                :downloadJSON="false"
                @updatePagination="searchAirdropped"
                @goToIncident="goToIncidentsCloud"
            />
          </template>
          <template v-else-if="item.key === 'onDevice'">
            <on-device-incidents-table
              :loading="!loading && loadingApiUnfinishedDevice"
              :total-items="apiUnfinishedDeviceTotal"
              :items="apiUnfinishedDevice"
              :show-client-column="false"
              @updatePagination="searchUnfinishedDevices"
              @goToDevice="goToDevice"
            />
          </template>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import _ from 'lodash';
import entireSearch from '@/api/entireSearch';
import IncidentsTable from '@/components/IncidentsSystemSearch/IncidentsTable';
import OnDeviceIncidentsTable from '@/components/IncidentsSystemSearch/OnDeviceIncidentsTable';
import Toolbar from '@/components/IncidentsSystemSearch/Toolbar';
import impersonateMixins from '@/mixins/impersonateMixins';
import { SET_TITLE } from '@/store/mutations';
import INCIDENT_CLOUD_TABS from '@/enums/incidentCloudTabs';
import ROUTES from '@/enums/routes';

export default {
  name: 'IncidentSystemSearchClient',
  components: {
    OnDeviceIncidentsTable,
    Toolbar,
    IncidentsTable,
  },

  mixins: [impersonateMixins],

  data() {
    return {
      tabIndex: 0,
      filters: {},
      loading: false,
      loadingIncidents: false,
      loadingTransmitted: false,
      loadingAirDropped: false,
      loadingApiUnfinishedDevice: false,
      incidentsTotal: 0,
      incidents: [],
      transmittedTotal: 0,
      transmitted: [],
      airdroppedTotal: 0,
      airdropped: [],
      apiUnfinishedDeviceTotal: 0,
      apiUnfinishedDevice: [],
    };
  },

  computed: {
    tabItems() {
      const items = [];
      items.push({ key: 'incidents', name: `Incidents (${this.incidentsTotal})`, disabled: this.incidentsTotal === 0 });
      items.push({ key: 'transmitted', name: `Transmitted (${this.transmittedTotal})`, disabled: this.transmittedTotal === 0 });

      // AirDropped tab: only for impersonated admin
      if (this.$can('seeAirDroppedTab', 'incidentSystemSearch') && this.$store.state.siteInfo.isImpersonated) {
        items.push({ key: 'airdropped', name: `AirDropped (${this.airdroppedTotal})`, disabled: this.airdroppedTotal === 0 });
      }

      // On Device tab: only for client admin and client captain user
      if (this.$can('seeOnDeviceTab', 'incidentSystemSearch')) {
        items.push({ key: 'onDevice', name: `On Device (${this.apiUnfinishedDeviceTotal})`, disabled: this.apiUnfinishedDeviceTotal === 0 });
      }

      return items;
    },
  },
  mounted() {
    this.$store.commit(SET_TITLE, 'Incident System Search (Client)');
  },

  methods: {
    /**
     * Search all entities.
     *
     * @param {Object} filters - Filters to search.
     */
    async search(filters) {
      this.filters = filters;
      try {
        this.loading = true;
        const promises = [
          this.searchIncidents(),
          this.searchTransmitted(),
        ];

        if (this.$can && this.$can('seeAirDroppedTab', 'incidentSystemSearch')) {
          promises.push(this.searchAirdropped());
        }

        if (this.$can && this.$can('seeOnDeviceTab', 'incidentSystemSearch')) {
          promises.push(this.searchUnfinishedDevices());
        }

        await Promise.all(promises);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Search regular/deleted/unfinished incidents.
     *
     * @param {Object} pagination - Pagination data.
     */
    async searchIncidents(pagination = {}) {
      try {
        this.loadingIncidents = true;
        const params = this.prepareParams(pagination);
        const response = await entireSearch.searchIncidents(params);
        this.incidents = this.setDownloadingProperty(response.results);
        this.incidentsTotal = response.pagination.total;
      } finally {
        this.loadingIncidents = false;
      }
    },

    /**
     * Search transmitted incidents.
     *
     * @param {Object} pagination - Pagination data.
     */
    async searchTransmitted(pagination = {}) {
      try {
        this.loadingTransmitted = true;
        const params = this.prepareParams(pagination);
        const response = await entireSearch.searchTransmitted(params);
        this.transmitted = this.setDownloadingProperty(response.results);
        this.transmittedTotal = response.pagination.total;
      } finally {
        this.loadingTransmitted = false;
      }
    },

    /**
     * Search airdropped incidents.
     *
     * @param {Object} pagination - Pagination data.
     */
    async searchAirdropped(pagination = {}) {
      try {
        this.loadingAirDropped = true;
        const params = this.prepareParams(pagination);
        const response = await entireSearch.searchAirdropped(params);
        this.airdropped = this.setDownloadingProperty(response.results);
        this.airdroppedTotal = response.pagination.total;
      } finally {
        this.loadingAirDropped = false;
      }
    },

    /**
     * Search Unfinished Device API logs.
     *
     * @param {Object} pagination - Pagination data.
     */
    async searchUnfinishedDevices(pagination = {}) {
      try {
        this.loadingApiUnfinishedDevice = true;
        const params = this.prepareParams(pagination);
        const response = await entireSearch.searchUnfinishedDevices(params);
        this.apiUnfinishedDevice = response.results;
        this.apiUnfinishedDeviceTotal = response.pagination.total;
      } finally {
        this.loadingApiUnfinishedDevice = false;
      }
    },

    /**
     * Prepare general params.
     *
     * @param {Object} pagination - Pagination object which.
     * @return {Object}
     */
    prepareParams(pagination = {}) {
      const params = _.cloneDeep(this.filters);
      params.page = pagination ? pagination.page : 1;
      params.per_page = pagination ? pagination.itemsPerPage : 25;

      return params;
    },

    setDownloadingProperty(items) {
      return items.map((item) => ({
        ...item,
        isDownloading: false,
      }));
    },

    goToIncident(incident) {
      if (incident.searchType === INCIDENT_CLOUD_TABS.UNFINISHED
        || incident.searchType === INCIDENT_CLOUD_TABS.DELETED) {
        this.goToIncidentsCloud(incident);

        return;
      }

      const routeData = this.$router.resolve({
        name: 'incidentView',
        params: { id: incident.id },
      });
      window.open(routeData.href, '_blank');
    },

    goToIncidentsCloud(incident) {
      let routeName = '';
      if (incident.searchType === INCIDENT_CLOUD_TABS.TRANSMITTED) {
        routeName = ROUTES.INCIDENTS_CLOUD_TRANSMITTED;
      } else if (incident.searchType === INCIDENT_CLOUD_TABS.AIRDROPPED) {
        routeName = ROUTES.INCIDENTS_CLOUD_AIRDROPPED;
      } else if (incident.searchType === INCIDENT_CLOUD_TABS.UNFINISHED) {
        routeName = ROUTES.INCIDENTS_CLOUD_UNFINISHED;
      } else if (incident.searchType === INCIDENT_CLOUD_TABS.DELETED) {
        routeName = ROUTES.INCIDENTS_CLOUD_DELETED;
      }

      const routeData = this.$router.resolve({
        name: routeName,
      });
      window.open(`${routeData.href}?sequenceNumber=${encodeURIComponent(incident.II_SequenceNumber)}`, '_blank');
    },

    goToDevice(incident) {
      const query = {
        search: incident.inventoryNumber,
      };

      const queryString = Object.keys(query)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
        .join('&');

      const routeData = this.$router.resolve({
        path: `/devices?${queryString}`,
      });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<style scoped>
.tab-item {
  letter-spacing: 0;
}
</style>
